import { __awaiter } from "tslib";
import { useState, useTransition } from "react";
import { useDebounceCallback, useEventCallback } from "usehooks-ts";
import { ConsoleLogger, LogLevels, } from "@plansoft-configuration-renderer/utils";
import { EditUpdateBehavior } from "../types/EditUpdateBehavior";
import { useCsAttributeApi } from "./useCsAttributeApi";
import { useLocalEditValue } from "./useLocalEditValue";
const logger = new ConsoleLogger("useControlledTextEdit", LogLevels.off);
// Number.MAX_SAFE_INTEGER throws an error in NodeJS timers
const MAX_SAFE_INT32 = 2147483647;
/**
 * Debounce mechanism for text edit fields.
 * @category Hook
 * @category CsAttribute
 * @param debounceMs
 * @param callback
 * @returns
 */
export function useControlledTextEdit(options) {
    var _a;
    const { onBackendValueSet, debounceDelay, updateBehavior } = options;
    // server and local edit value and api
    const value = useLocalEditValue();
    const attributeApi = useCsAttributeApi();
    const [isFocused, setIsFocused] = useState(false);
    const [isTransitioning, startTransition] = useTransition();
    // this is the value used while typing to avoid menu redux state updates
    const [temporaryValue, setTemporaryValue] = useState(undefined);
    // this function must not be async, else this affects this affects flushing of the debounce queue
    const setValue = useEventCallback((newValue) => {
        logger.log("setValue", { newValue });
        // we need that because setValue is async, this affects flushing of the debounce queue
        attributeApi.setLocalValue(newValue);
        // sets the value as local edit value and in the backend
        attributeApi.setValue(newValue).then(onBackendValueSet);
    });
    // we always use this, but we can set the delay to a very high value
    // we flush the debounce queue on blur to submit any changes
    // this saves us from having to detect value change in value in onBlur
    const setValueDebounced = useDebounceCallback(setValue, updateBehavior === EditUpdateBehavior.OnChange
        ? debounceDelay
        : MAX_SAFE_INT32);
    const handleValueChange = useEventCallback((newValue) => {
        setTemporaryValue(newValue); // this ne
        // if we need more state setters here, then we should use startTransaction
        setValueDebounced(newValue);
    });
    const handleChange = useEventCallback((event) => {
        handleValueChange(event.currentTarget.value);
    });
    const handleReset = useEventCallback(() => __awaiter(this, void 0, void 0, function* () {
        yield attributeApi.resetAttribute();
    }));
    const handleBlur = useEventCallback(() => {
        startTransition(() => {
            setValueDebounced.flush();
            setIsFocused(false);
            setTemporaryValue(undefined);
        });
    });
    const handleFocus = useEventCallback(() => {
        setIsFocused(true);
    });
    if (isFocused) {
        logger.debug({ value, temporaryValue, isTransitioning });
    }
    return {
        value: (_a = temporaryValue !== null && temporaryValue !== void 0 ? temporaryValue : value) !== null && _a !== void 0 ? _a : "",
        isFocused,
        handleValueChange,
        handleChange,
        handleReset,
        handleBlur,
        handleFocus,
    };
}
//# sourceMappingURL=useControlledTextEdit.js.map